import React from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  Result,
  Row,
  Select,
  Space,
  Steps,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  BellOutlined,
  CodeOutlined,
  NotificationOutlined,
  ReadOutlined,
  SendOutlined,
  ShoppingOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useSendPushBroadcast, useSendPushToUser } from '@shared/api/hooks/useNotifications';
import { useMatches } from '@shared/api/hooks/useMatches';
import { useProducts } from '@shared/api/hooks/useProducts';
import { usePosts } from '@shared/api/hooks/usePosts';
import { ScheduledPushPanel } from './ScheduledPushPanel';
import { useUsers } from '@shared/api/hooks/useUsers';

type ActionKind = 'none' | 'match' | 'product' | 'post' | 'tab' | 'order' | 'receipt' | 'raw';

interface FormValues {
  titleIt: string;
  titleEn?: string;
  bodyIt: string;
  bodyEn?: string;
  action: ActionKind;
  recipientUserId?: string;
  matchId?: string;
  productId?: string;
  postId?: string;
  tabScreen?: string;
  orderId?: number;
  receiptId?: number;
  rawJson?: string;
}

/**
 * Modelli (template) di notifica per tenant non-sviluppatori. Ogni modello
 * pre-imposta l'azione tecnica al tocco (`action`) e suggerisce titolo/corpo,
 * così l'admin non deve conoscere il payload `data`. Il modello "advanced"
 * espone l'editor JSON grezzo per casi rari (resta nascosto dietro una scelta
 * esplicita).
 */
interface Template {
  key: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  action: ActionKind;
  defaults?: { titleIt?: string; bodyIt?: string; tabScreen?: string };
  advanced?: boolean;
  /** T-156 — categoria "un solo tifoso": richiede la scelta del destinatario. */
  singleUser?: boolean;
}

const TEMPLATES: Template[] = [
  {
    key: 'announcement',
    icon: <NotificationOutlined />,
    label: 'Avviso generale',
    description: 'Un messaggio per tutti i tifosi. Al tocco non apre nessuna schermata.',
    action: 'none',
  },
  {
    key: 'shop_product',
    icon: <ShoppingOutlined />,
    label: 'Promo prodotto',
    description: 'Spingi un prodotto dello shop. Al tocco apre la scheda prodotto.',
    action: 'product',
    defaults: {
      titleIt: 'Nuovo nello shop! 🛍️',
      bodyIt: 'Scopri il nuovo arrivo. Tocca per vederlo.',
    },
  },
  {
    key: 'match',
    icon: <TrophyOutlined />,
    label: 'Partita',
    description: 'Annuncia una partita. Al tocco apre la pagina partita.',
    action: 'match',
    defaults: {
      titleIt: 'Match day! ⚽',
      bodyIt: 'Si avvicina la partita. Tocca per i dettagli.',
    },
  },
  {
    key: 'news',
    icon: <ReadOutlined />,
    label: 'Nuova news',
    description: 'Annuncia un articolo. Al tocco apre la news.',
    action: 'post',
    defaults: {
      titleIt: 'Nuova news 📰',
      bodyIt: 'Abbiamo pubblicato un aggiornamento. Leggi ora.',
    },
  },
  {
    key: 'section',
    icon: <AppstoreOutlined />,
    label: 'Apri una sezione',
    description: "Porta i tifosi a una sezione dell'app (Shop, Classifica, …).",
    action: 'tab',
  },
  {
    key: 'single_user',
    icon: <UserOutlined />,
    label: 'Un solo tifoso',
    description: 'Invia a un singolo tifoso scelto da te. Solo lui la riceve.',
    action: 'none',
    singleUser: true,
  },
  {
    key: 'advanced',
    icon: <CodeOutlined />,
    label: 'Avanzato (JSON)',
    description: 'Per sviluppatori. Payload personalizzato.',
    action: 'raw',
    advanced: true,
  },
];

const TAB_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'Home', label: 'Home' },
  { value: 'Match', label: 'Partita corrente' },
  { value: 'Loyalty', label: 'Loyalty / Punti' },
  { value: 'Shop', label: 'Shop' },
  { value: 'Fan', label: 'Fan Zone' },
  { value: 'Standings', label: 'Classifica' },
  { value: 'Squad', label: 'Rosa' },
  { value: 'Leaderboard', label: 'Leaderboard tifosi' },
  { value: 'Partners', label: 'Partners' },
  { value: 'Profile', label: 'Profilo' },
  { value: 'Notifications', label: 'Notifiche' },
];

/** Traduce la scelta del modello nel payload `data` tecnico inviato a FCM/Expo. */
function buildPayload(v: FormValues): Record<string, string> | undefined {
  switch (v.action) {
    case 'none':
      return undefined;
    case 'match':
      return v.matchId ? { matchId: v.matchId } : undefined;
    case 'product':
      return v.productId
        ? {
            screen: 'ProductDetail',
            params: JSON.stringify({ productId: v.productId }),
          }
        : undefined;
    case 'post':
      return v.postId ? { postId: v.postId } : undefined;
    case 'tab':
      return v.tabScreen ? { screen: v.tabScreen } : undefined;
    case 'order':
      return v.orderId != null ? { orderId: String(v.orderId) } : undefined;
    case 'receipt':
      return v.receiptId != null ? { receiptId: String(v.receiptId) } : undefined;
    case 'raw': {
      if (!v.rawJson || !v.rawJson.trim()) return undefined;
      const parsed: unknown = JSON.parse(v.rawJson);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('Atteso oggetto JSON');
      }
      const out: Record<string, string> = {};
      for (const [k, val] of Object.entries(parsed as Record<string, unknown>)) {
        out[k] = typeof val === 'string' ? val : JSON.stringify(val);
      }
      return out;
    }
  }
}

function pickLocale(field: { it?: string; en?: string } | undefined): string {
  if (!field) return '';
  return field.it ?? field.en ?? '';
}

/** Campi target richiesti da ciascuna azione (per validazione per-step). */
function targetFieldsFor(a: ActionKind): Array<keyof FormValues> {
  switch (a) {
    case 'match':
      return ['matchId'];
    case 'product':
      return ['productId'];
    case 'post':
      return ['postId'];
    case 'tab':
      return ['tabScreen'];
    case 'order':
      return ['orderId'];
    case 'receipt':
      return ['receiptId'];
    case 'raw':
      return ['rawJson'];
    default:
      return [];
  }
}

/** Frase che descrive cosa succede al tocco della notifica (per anteprima/alert). */
function summarize(v: Partial<FormValues>): string {
  switch (v.action) {
    case 'none':
      return 'Nessuna azione al tocco';
    case 'match':
      return v.matchId ? 'Apre la partita selezionata' : 'Apre la partita';
    case 'product':
      return 'Apre la scheda prodotto';
    case 'post':
      return 'Apre la news';
    case 'tab':
      return `Apre la sezione ${v.tabScreen ?? ''}`.trim();
    case 'order':
      return "Apre l'ordine";
    case 'receipt':
      return 'Apre la ricevuta';
    case 'raw':
      return 'Payload personalizzato';
    default:
      return '';
  }
}

function ImmediatePushWizard(): React.ReactElement {
  const [form] = Form.useForm<FormValues>();
  const sendMutation = useSendPushBroadcast();
  const sendUserMutation = useSendPushToUser();
  const [step, setStep] = React.useState(0);
  const [templateKey, setTemplateKey] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<{ recipientCount?: number; single?: boolean } | null>(
    null,
  );
  const isSingleUser = templateKey === 'single_user';
  // T-156 — ricerca destinatario per la categoria "un solo tifoso". La lista
  // riusa l'endpoint tifosi esistente (ricerca per nome/email).
  const [recipientSearch, setRecipientSearch] = React.useState('');
  const usersQ = useUsers({ search: recipientSearch || undefined, limit: 20 });
  // Snapshot dei valori COMPLETI catturato al passaggio allo step Anteprima.
  // I Form.Item dello step "Messaggio" si smontano allo step Anteprima: né
  // Form.useWatch né form.validateFields()/getFieldsValue vedono i campi
  // smontati (ritornano undefined). Lo snapshot preso mentre i campi sono
  // montati è l'unica fonte affidabile sia per l'anteprima sia per l'invio.
  // (Prima l'invio rileggeva i campi smontati -> title/body/data vuoti.)
  const [snapshot, setSnapshot] = React.useState<FormValues | null>(null);

  const action = (Form.useWatch('action', form) ?? 'none') as ActionKind;
  const rawJson = Form.useWatch('rawJson', form);
  // Watch dei target per il riepilogo live nello step "Messaggio" (campi montati).
  const matchId = Form.useWatch('matchId', form);
  const tabScreen = Form.useWatch('tabScreen', form);

  const matchesQ = useMatches({ limit: 50 });
  const productsQ = useProducts({ limit: 50 });
  const postsQ = usePosts({ limit: 50, includeDrafts: false });

  const rawJsonStatus = React.useMemo<
    { ok: true; key: string } | { ok: false; err: string } | null
  >(() => {
    if (action !== 'raw') return null;
    if (!rawJson || !rawJson.trim()) return null;
    try {
      const parsed: unknown = JSON.parse(rawJson);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return { ok: false, err: 'Atteso oggetto JSON (es. {"matchId":"123"})' };
      }
      const keys = Object.keys(parsed as Record<string, unknown>);
      const known = ['matchId', 'postId', 'orderId', 'receiptId', 'productId', 'screen'];
      const matched = keys.find((k) => known.includes(k));
      return { ok: true, key: matched ?? 'nessuna riconosciuta' };
    } catch (e) {
      return { ok: false, err: (e as Error).message };
    }
  }, [action, rawJson]);

  const selectTemplate = (t: Template): void => {
    setTemplateKey(t.key);
    form.setFieldsValue({
      action: t.action,
      titleIt: t.defaults?.titleIt ?? '',
      titleEn: undefined,
      bodyIt: t.defaults?.bodyIt ?? '',
      bodyEn: undefined,
      recipientUserId: undefined,
      matchId: undefined,
      productId: undefined,
      postId: undefined,
      tabScreen: t.defaults?.tabScreen,
      orderId: undefined,
      receiptId: undefined,
      rawJson: undefined,
    });
    setStep(1);
  };

  const goToContent = async (): Promise<void> => {
    try {
      await form.validateFields([
        'titleIt',
        'bodyIt',
        ...(isSingleUser ? (['recipientUserId'] as Array<keyof FormValues>) : []),
        ...targetFieldsFor(action),
      ]);
    } catch {
      return;
    }
    if (action === 'raw' && rawJsonStatus && !rawJsonStatus.ok) return;
    // Cattura i valori COMPLETI ora che i campi sono montati: lo step Anteprima
    // li smonta e validateFields()/getFieldsValue li perderebbe (-> invio vuoto).
    setSnapshot(form.getFieldsValue(true) as FormValues);
    setStep(2);
  };

  const restart = (): void => {
    form.resetFields();
    setTemplateKey(null);
    setSnapshot(null);
    setStep(0);
    setResult(null);
  };

  const onSend = async (): Promise<void> => {
    // Qui i campi dello step Messaggio sono smontati: validateFields()/
    // getFieldsValue tornerebbero vuoti. Uso lo snapshot catturato in
    // goToContent (campi montati e già validati).
    const v = snapshot;
    if (!v) {
      setStep(1);
      return;
    }
    let data: Record<string, string> | undefined;
    try {
      data = buildPayload(v);
    } catch (e) {
      form.setFields([{ name: 'rawJson', errors: [`JSON non valido: ${(e as Error).message}`] }]);
      setStep(1);
      return;
    }
    const payload = {
      title: { it: v.titleIt, ...(v.titleEn ? { en: v.titleEn } : {}) },
      body: { it: v.bodyIt, ...(v.bodyEn ? { en: v.bodyEn } : {}) },
      ...(data ? { data } : {}),
    };
    try {
      if (isSingleUser) {
        if (!v.recipientUserId) {
          setStep(1);
          return;
        }
        await sendUserMutation.mutateAsync({ ...payload, userId: v.recipientUserId });
        setResult({ single: true });
      } else {
        const res = await sendMutation.mutateAsync(payload);
        setResult({ recipientCount: res.recipientCount });
      }
      message.success('Push inviato in coda');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore di rete'));
    }
  };

  // Riepilogo live per l'alert dello step "Messaggio" (campi target montati).
  const actionSummary = (): string => summarize({ action, matchId, tabScreen });

  // ─── Risultato finale ──────────────────────────────────────────────────────
  if (result) {
    return (
      <Card>
        <Result
          status="success"
          title="Push in coda"
          subTitle={
            result.single
              ? 'Inviata al tifoso selezionato.'
              : result.recipientCount != null
                ? `Destinatari stimati: ${result.recipientCount}`
                : 'Push enqueued.'
          }
          extra={[
            <Button key="another" type="primary" onClick={restart}>
              Nuovo invio
            </Button>,
          ]}
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={24}>
      <Card>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Invio push broadcast
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
          Tutti i tifosi attivi con push token ricevono la notifica. Filtri per categoria rispettati
          (notifPrefs <code>CUSTOM</code>).
        </Typography.Paragraph>

        <Steps
          current={step}
          style={{ marginBottom: 24 }}
          items={[{ title: 'Modello' }, { title: 'Messaggio' }, { title: 'Anteprima & invio' }]}
        />

        {/* ─── STEP 0: scelta modello ─────────────────────────────────────── */}
        {step === 0 && (
          <>
            <Typography.Paragraph>
              Scegli cosa vuoi comunicare. Pensiamo noi a dove portare il tifoso quando tocca la
              notifica.
            </Typography.Paragraph>
            <Row gutter={[16, 16]}>
              {TEMPLATES.map((t) => (
                <Col xs={24} sm={12} lg={8} key={t.key}>
                  <Card
                    hoverable
                    onClick={() => selectTemplate(t)}
                    style={t.advanced ? { borderStyle: 'dashed' } : undefined}
                    styles={{ body: { minHeight: 132 } }}
                  >
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      <Typography.Text style={{ fontSize: 26 }}>{t.icon}</Typography.Text>
                      <Typography.Text strong>{t.label}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                        {t.description}
                      </Typography.Text>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}

        {/* ─── Form (step 1 e 2) ──────────────────────────────────────────── */}
        <Form
          form={form}
          layout="vertical"
          initialValues={{ action: 'none' as ActionKind }}
          style={{ display: step === 0 ? 'none' : 'block' }}
        >
          {/* action è gestito dai modelli: campo nascosto per tenerne il valore */}
          <Form.Item name="action" hidden>
            <Input />
          </Form.Item>

          {step === 1 && (
            <>
              <Alert
                type="info"
                showIcon
                message={TEMPLATES.find((t) => t.key === templateKey)?.label ?? 'Messaggio'}
                description={actionSummary()}
                style={{ marginBottom: 16 }}
              />

              {isSingleUser && (
                <Form.Item
                  name="recipientUserId"
                  label="Destinatario"
                  rules={[{ required: true, message: 'Seleziona il tifoso destinatario' }]}
                >
                  <Select
                    showSearch
                    loading={usersQ.isLoading}
                    placeholder="Cerca un tifoso per nome o email"
                    filterOption={false}
                    onSearch={setRecipientSearch}
                    notFoundContent={usersQ.isLoading ? 'Ricerca…' : 'Nessun tifoso'}
                    options={(usersQ.data?.data ?? []).map((u) => ({
                      value: u.id,
                      label: `${u.displayName || '(senza nome)'} — ${u.email}`,
                    }))}
                  />
                </Form.Item>
              )}

              <Form.Item name="titleIt" label="Titolo (IT)" rules={[{ required: true, max: 200 }]}>
                <Input placeholder="Offerta!" />
              </Form.Item>
              <Form.Item name="bodyIt" label="Testo (IT)" rules={[{ required: true }]}>
                <Input.TextArea rows={3} placeholder="Sconto 20% su tutte le maglie." />
              </Form.Item>

              <Collapse
                ghost
                items={[
                  {
                    key: 'en',
                    label: 'Traduzione inglese (opzionale)',
                    children: (
                      <>
                        <Form.Item name="titleEn" label="Titolo (EN)">
                          <Input placeholder="Special offer!" />
                        </Form.Item>
                        <Form.Item name="bodyEn" label="Testo (EN)">
                          <Input.TextArea rows={3} placeholder="20% off all jerseys." />
                        </Form.Item>
                      </>
                    ),
                  },
                ]}
              />

              {/* ─── Destinazione contestuale al modello ─────────────────── */}
              {action === 'match' && (
                <Form.Item
                  name="matchId"
                  label="Partita da aprire"
                  rules={[{ required: true, message: 'Seleziona una partita' }]}
                >
                  <Select
                    showSearch
                    loading={matchesQ.isLoading}
                    placeholder="Es. Lanciano vs Pescara - 22/05"
                    optionFilterProp="label"
                    options={(matchesQ.data?.data ?? []).map((m) => ({
                      value: m.id,
                      label: `${m.homeTeamName} vs ${m.awayTeamName} — ${new Date(
                        m.date,
                      ).toLocaleDateString('it-IT')}`,
                    }))}
                  />
                </Form.Item>
              )}

              {action === 'product' && (
                <Form.Item
                  name="productId"
                  label="Prodotto da aprire"
                  rules={[{ required: true, message: 'Seleziona un prodotto' }]}
                >
                  <Select
                    showSearch
                    loading={productsQ.isLoading}
                    placeholder="Cerca per nome prodotto"
                    optionFilterProp="label"
                    options={(productsQ.data?.data ?? []).map((p) => ({
                      value: p.id,
                      label: `${pickLocale(p.name) || p.sku} — ${p.category}`,
                    }))}
                  />
                </Form.Item>
              )}

              {action === 'post' && (
                <Form.Item
                  name="postId"
                  label="Post da aprire"
                  rules={[{ required: true, message: 'Seleziona un post' }]}
                >
                  <Select
                    showSearch
                    loading={postsQ.isLoading}
                    placeholder="Cerca per titolo"
                    optionFilterProp="label"
                    options={(postsQ.data?.data ?? []).map((p) => ({
                      value: p.id,
                      label: `${pickLocale(p.title) || '(senza titolo)'} — ${p.category}`,
                    }))}
                  />
                </Form.Item>
              )}

              {action === 'tab' && (
                <Form.Item
                  name="tabScreen"
                  label="Sezione dell'app"
                  rules={[{ required: true, message: 'Seleziona una sezione' }]}
                >
                  <Select options={TAB_OPTIONS} placeholder="Es. Shop" />
                </Form.Item>
              )}

              {action === 'order' && (
                <Form.Item
                  name="orderId"
                  label="ID ordine"
                  rules={[{ required: true, message: 'Inserisci ID ordine' }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} placeholder="42" />
                </Form.Item>
              )}

              {action === 'receipt' && (
                <Form.Item
                  name="receiptId"
                  label="ID ricevuta"
                  rules={[{ required: true, message: 'Inserisci ID ricevuta' }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} placeholder="42" />
                </Form.Item>
              )}

              {action === 'raw' && (
                <Collapse
                  defaultActiveKey={['raw']}
                  items={[
                    {
                      key: 'raw',
                      label: 'Editor JSON',
                      children: (
                        <>
                          <Alert
                            type="warning"
                            showIcon
                            message="Modalità sviluppatore"
                            description={
                              <span>
                                Chiavi valide: <code>matchId</code>, <code>postId</code>,{' '}
                                <code>orderId</code>, <code>receiptId</code>, <code>productId</code>
                                , <code>screen</code> (+ <code>params</code>).
                              </span>
                            }
                            style={{ marginBottom: 12 }}
                          />
                          <Form.Item
                            name="rawJson"
                            label="JSON payload"
                            validateStatus={rawJsonStatus && !rawJsonStatus.ok ? 'error' : ''}
                            help={
                              rawJsonStatus
                                ? rawJsonStatus.ok
                                  ? `JSON valido — chiave riconosciuta: ${rawJsonStatus.key}`
                                  : `JSON non valido: ${rawJsonStatus.err}`
                                : undefined
                            }
                          >
                            <Input.TextArea
                              rows={6}
                              placeholder='{"screen":"ProductDetail","params":{"productId":"abc"}}'
                              style={{ fontFamily: 'monospace' }}
                            />
                          </Form.Item>
                        </>
                      ),
                    },
                  ]}
                />
              )}

              <Space style={{ marginTop: 8 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => setStep(0)}>
                  Indietro
                </Button>
                <Button type="primary" icon={<ArrowRightOutlined />} onClick={goToContent}>
                  Anteprima
                </Button>
              </Space>
            </>
          )}

          {/* ─── STEP 2: anteprima + invio ─────────────────────────────── */}
          {step === 2 && (
            <>
              <Alert
                message={
                  isSingleUser
                    ? 'Notifica a un solo tifoso'
                    : 'Operazione visibile a tutti i tifosi'
                }
                description={
                  isSingleUser
                    ? "La riceve solo il tifoso selezionato. L'invio è irreversibile: il backend mette il job in coda e processa in background."
                    : "L'invio è irreversibile. Il backend mette il job in coda e processa in background."
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Typography.Text type="secondary">Anteprima notifica</Typography.Text>
              <Card
                size="small"
                style={{ maxWidth: 420, marginTop: 8, marginBottom: 16, background: '#fafafa' }}
              >
                <Space align="start">
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: '#e5252b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <BellOutlined style={{ color: '#fff', fontSize: 18 }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <Typography.Text strong style={{ display: 'block' }}>
                      {snapshot?.titleIt || '(titolo)'}
                    </Typography.Text>
                    <Typography.Text style={{ display: 'block' }}>
                      {snapshot?.bodyIt || '(testo)'}
                    </Typography.Text>
                    <Tag style={{ marginTop: 6 }}>{snapshot ? summarize(snapshot) : ''}</Tag>
                  </div>
                </Space>
              </Card>

              <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={() => setStep(1)}>
                  Modifica
                </Button>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={onSend}
                  loading={sendMutation.isPending || sendUserMutation.isPending}
                  size="large"
                >
                  {isSingleUser ? 'Invia al tifoso' : 'Invia broadcast'}
                </Button>
              </Space>
            </>
          )}
        </Form>
      </Card>
    </Space>
  );
}

/**
 * T-209 — la pagina Push ora ha due schede: l'invio immediato (wizard esistente,
 * invariato) e le notifiche programmate (nuova area con lista + Invia ora,
 * Modifica, Ripianifica, Elimina).
 */
export function PushPage(): React.ReactElement {
  return (
    <Tabs
      defaultActiveKey="now"
      items={[
        { key: 'now', label: 'Invio immediato', children: <ImmediatePushWizard /> },
        { key: 'scheduled', label: 'Programmate', children: <ScheduledPushPanel /> },
      ]}
    />
  );
}
